/**
 * Semana da turma — a unidade de "encontro" da catequese.
 *
 * Por padrão é a semana ISO (segunda a domingo). Turmas cujo dia principal cai
 * no fim da semana ISO e a reposição na semana seguinte (ex.: Sáb + Qua de
 * reposição) definem um `inicioCiclo` próprio (0=Dom … 6=Sáb): o "encontro"
 * passa a ser a janela de 7 dias começando nesse dia — Sáb 11/07 + Qua 15/07
 * caem no MESMO ciclo sáb→sex. Com `inicioCiclo = 1` (default) o comportamento
 * é exatamente a semana ISO.
 */
/** Início de ciclo padrão: segunda-feira (semana ISO). */
export const INICIO_CICLO_PADRAO = 1;
/**
 * Data de "hoje" (YYYY-MM-DD) fixada em UTC-3 (horário de Brasília, sem DST).
 * Toda noção de "hoje/já passou" do sistema DEVE usar esta função — nunca o
 * fuso do dispositivo ou UTC puro.
 */
export function hojeUtc3(agora = new Date()) {
    return new Date(agora.getTime() - 3 * 3600 * 1000).toISOString().slice(0, 10);
}
function fromISO(dataISO) {
    return new Date(dataISO + 'T00:00:00Z');
}
function toISO(d) {
    return d.toISOString().slice(0, 10);
}
/** Posição do dia dentro do ciclo (0 = dia de início … 6 = último dia). */
function posicaoNoCiclo(diaSemana, inicioCiclo) {
    return (diaSemana - inicioCiclo + 7) % 7;
}
/**
 * Chave única da semana da turma que contém a data. Com o ciclo padrão
 * (segunda) usa o formato ISO '2026-W30'; com ciclo próprio, a data de início
 * da janela (só precisa ser única por ciclo — não é exibida ao usuário).
 */
export function semanaKey(dataISO, inicioCiclo = INICIO_CICLO_PADRAO) {
    if (inicioCiclo !== INICIO_CICLO_PADRAO) {
        return intervaloSemana(dataISO, inicioCiclo).inicio;
    }
    const d = fromISO(dataISO);
    const dia = d.getUTCDay() || 7; // 1=Seg … 7=Dom
    const quinta = new Date(d);
    quinta.setUTCDate(d.getUTCDate() + 4 - dia);
    const ano = quinta.getUTCFullYear();
    const inicioAno = Date.UTC(ano, 0, 1);
    const semana = Math.ceil(((quinta.getTime() - inicioAno) / 86400000 + 1) / 7);
    return `${ano}-W${String(semana).padStart(2, '0')}`;
}
/** Primeiro e último dia da semana da turma (janela de 7 dias) que contém a data. */
export function intervaloSemana(dataISO, inicioCiclo = INICIO_CICLO_PADRAO) {
    const d = fromISO(dataISO);
    const inicio = new Date(d);
    inicio.setUTCDate(d.getUTCDate() - posicaoNoCiclo(d.getUTCDay(), inicioCiclo));
    const fim = new Date(inicio);
    fim.setUTCDate(inicio.getUTCDate() + 6);
    return { inicio: toISO(inicio), fim: toISO(fim) };
}
/**
 * Data (YYYY-MM-DD) em que um dia da semana (0=Dom…6=Sáb) cai dentro da semana
 * da turma que contém `dataISO`. O dia anterior ao início do ciclo é o ÚLTIMO
 * da janela (no ciclo padrão, domingo).
 */
export function dataDoDiaNaSemana(dataISO, diaSemana, inicioCiclo = INICIO_CICLO_PADRAO) {
    const { inicio } = intervaloSemana(dataISO, inicioCiclo);
    const d = fromISO(inicio);
    d.setUTCDate(d.getUTCDate() + posicaoNoCiclo(diaSemana, inicioCiclo));
    return toISO(d);
}
/** Soma dias a uma data YYYY-MM-DD. */
export function addDias(dataISO, dias) {
    const d = fromISO(dataISO);
    d.setUTCDate(d.getUTCDate() + dias);
    return toISO(d);
}
/**
 * Dias de aula da turma na sua semana corrente, em ordem cronológica dentro do
 * ciclo, com a data resolvida e se já ocorreram. Base do lançamento de chamada:
 * a data é derivada de semana+dia, nunca digitada livremente.
 */
export function diasDaSemanaAtual(diasSemanaTurma, hojeISO, inicioCiclo = INICIO_CICLO_PADRAO) {
    return [...diasSemanaTurma]
        .sort((a, b) => posicaoNoCiclo(a, inicioCiclo) - posicaoNoCiclo(b, inicioCiclo))
        .map((diaSemana) => {
        const data = dataDoDiaNaSemana(hojeISO, diaSemana, inicioCiclo);
        return { diaSemana, data, ocorrido: data <= hojeISO };
    });
}
//# sourceMappingURL=semana.js.map